import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { captureEvent } from '../lib/analytics';
import VideoUpload from '../components/VideoUpload';
import ProcessingStatus from '../components/ProcessingStatus';
import FitResultsPage from '../components/fitresults/FitResultsPage';
import { ProcessingPipeline, SimpleFrame } from '../modules/ProcessingPipeline';
import { BikeType, RidingStyle, RidingExperience, Flexibility, PainPoint } from '../constants/bikeConfig';
import { useApiClient } from '../services/apiClient';
import { loadAnalysisState, clearSavedAnalysisState } from '../utils/sessionPersistence';
import { buildPainDetail, heightToCm, weightToKg } from './onboardingMapping';

type DashboardState = 'upload' | 'processing' | 'results';

const LOCAL_STORAGE_KEY = 'bikendure_bike_profile';

// Reverse maps: backend API values → frontend types
const BIKE_TYPE_REVERSE: Record<string, BikeType> = {
  road:        'road',
  mountain:    'mountain',
  gravel:      'gravel',
  trekking:    'trekking',
  'time-trial':'triathlon',
};

const EXPERIENCE_REVERSE: Record<string, RidingExperience> = {
  beginner:     'beginner',
  intermediate: 'intermediate',
  advanced:     'experienced',
};

const FLEXIBILITY_REVERSE: Record<string, Flexibility> = {
  high:     'good',
  moderate: 'average',
  low:      'limited',
};

const POSITION_REVERSE: Record<string, RidingStyle> = {
  aggressive: 'performance',
  moderate:   'balanced',
  relaxed:    'comfort',
};

const DashboardPage: React.FC = () => {
  const { isSignedIn, isLoaded, getToken } = useAuth();
  const navigate = useNavigate();
  const api = useApiClient();
  const [dashState, setDashState] = useState<DashboardState>('upload');
  // hasBikeFit (i.e. isPremium) is no longer read here — FitResultsPage now
  // derives entitlement from the fit-report payload itself (report.entitled),
  // not a prop threaded down from billing status. The setter is still needed:
  // it's how the initial billing-status fetch primes the credit, and how
  // generating a PDF (which consumes the credit) is reflected back into it
  // via onCreditConsumed.
  const [, setHasBikeFit] = useState(false);
  // isAdmin still comes back from billing status (and gates the admin-only
  // FrameGallery, which this page no longer mounts — see task-15 brief).
  // Kept as write-only for now rather than removed, so re-adding the gallery
  // behind isAdmin later doesn't require re-plumbing this fetch.
  const [, setIsAdmin] = useState(false);

  const [bikeType, setBikeType]       = useState<BikeType>('road');
  const [ridingStyle, setRidingStyle] = useState<RidingStyle>('balanced');
  const [experience, setExperience]   = useState<RidingExperience>('intermediate');
  const [flexibility, setFlexibility] = useState<Flexibility>('average');
  const [painPoints, setPainPoints]   = useState<PainPoint[]>(['none']);

  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingStage, setProcessingStage]       = useState('');
  const [sessionId, setSessionId]                   = useState<string>('');
  const [sideFrames, setSideFrames]                 = useState<SimpleFrame[]>([]);
  const [frontFrames, setFrontFrames]               = useState<SimpleFrame[]>([]);
  const [pedalAnalysis, setPedalAnalysis]           = useState<any>(undefined);
  const [processingError, setProcessingError]       = useState<string | null>(null);

  // ── Load profile from localStorage immediately (works for anon + fallback) ──
  useEffect(() => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.bikeType)    setBikeType(parsed.bikeType);
        if (parsed.ridingStyle) setRidingStyle(parsed.ridingStyle);
        if (parsed.experience)  setExperience(parsed.experience);
        if (parsed.flexibility) setFlexibility(parsed.flexibility);
        if (parsed.painPoints)  setPainPoints(parsed.painPoints);
      }
    } catch { /* ignore */ }
  }, []);

  // ── Once auth resolves and user is signed in, upgrade to backend data ──
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;

    api.getBillingStatus()
      .then(s => {
        setHasBikeFit(s.has_bike_fit);
        setIsAdmin(s.role === 'admin');
      })
      .catch((err) => {
        console.error('Failed to load billing status:', err);
      });

    api.getBikeProfile()
      .then(profile => {
        if (profile.bike_type)           setBikeType(BIKE_TYPE_REVERSE[profile.bike_type] ?? 'road');
        if (profile.position_preference) setRidingStyle(POSITION_REVERSE[profile.position_preference] ?? 'balanced');
        if (profile.experience_level)    setExperience(EXPERIENCE_REVERSE[profile.experience_level] ?? 'intermediate');
        if (profile.flexibility_level)   setFlexibility(FLEXIBILITY_REVERSE[profile.flexibility_level] ?? 'average');
        if (profile.pain_points) {
          const points = profile.pain_points.split(',').filter(Boolean) as PainPoint[];
          setPainPoints(points.length > 0 ? points : ['none']);
        }
      })
      .catch(() => {});

    // Sync anonymous profile to backend if present
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        api.saveBikeProfile({
          bike_type: parsed.bikeType === 'triathlon' ? 'time-trial' : parsed.bikeType,
          experience_level: ({ beginner: 'beginner', intermediate: 'intermediate', experienced: 'advanced' } as any)[parsed.experience] ?? 'intermediate',
          flexibility_level: ({ good: 'high', average: 'moderate', limited: 'low' } as any)[parsed.flexibility] ?? 'moderate',
          position_preference: ({ performance: 'aggressive', balanced: 'moderate', comfort: 'relaxed' } as any)[parsed.ridingStyle] ?? 'moderate',
          pain_points: parsed.painPoints?.join(',') ?? 'none',
          pain_detail: buildPainDetail(parsed.pains ?? {}, parsed.painFree ?? false, parsed.painOther ?? ''),
          height_cm: heightToCm(parsed.height ?? '', parsed.units ?? 'metric'),
          weight_kg: weightToKg(parsed.weight ?? '', parsed.units ?? 'metric'),
          units: parsed.units ?? 'metric',
        }).then(() => {
          localStorage.removeItem(LOCAL_STORAGE_KEY);
        }).catch(() => {});
      }
    } catch { /* ignore */ }
  }, [isLoaded, isSignedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Restore analysis state from sessionStorage (returning from auth/billing) ──
  useEffect(() => {
    loadAnalysisState().then(restored => {
      if (restored) {
        setSessionId(restored.sessionId);
        setSideFrames(restored.sideFrames);
        setFrontFrames(restored.frontFrames);
        setPedalAnalysis(restored.pedalAnalysis);
        setBikeType(restored.bikeType);
        setRidingStyle(restored.ridingStyle);
        setExperience(restored.experience);
        setFlexibility(restored.flexibility);
        setPainPoints(restored.painPoints);
        setDashState('results');
      }
    });
  }, []);

  const handleVideosUploaded = async (
    sideVideo: File | null,
    frontVideo: File | null
  ) => {
    captureEvent('analysis_started');
    setDashState('processing');
    setProcessingProgress(0);
    setProcessingError(null);
    clearSavedAnalysisState();

    try {
      const pipeline = new ProcessingPipeline();
      const result = await pipeline.processVideos(
        sideVideo,
        frontVideo,
        (progress: number, stage: string) => {
          setProcessingProgress(progress);
          setProcessingStage(stage);
        },
        getToken
      );

      setSessionId(result.sessionId);
      setSideFrames(result.sideFrames);
      setFrontFrames(result.frontFrames);
      setPedalAnalysis(result.pedalAnalysis);
      captureEvent('analysis_completed');
      setDashState('results');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      captureEvent('analysis_error', { error: errorMessage });
      setProcessingError(errorMessage);
      alert(
        `Error processing videos: ${errorMessage}\n\nPlease ensure:\n- Videos are in a supported format (MP4, MOV, etc.)\n- Videos are not corrupted\n- Videos have sufficient duration`
      );
      setDashState('upload');
    }
  };

  const handleStartOver = () => {
    setDashState('upload');
    setSessionId('');
    setSideFrames([]);
    setFrontFrames([]);
    setPedalAnalysis(undefined);
    setProcessingProgress(0);
    setProcessingError(null);
    clearSavedAnalysisState();
  };

  return (
    <>
      {dashState === 'upload' && (
        <>
          <VideoUpload
            onVideosUploaded={handleVideosUploaded}
            onBack={() => navigate('/onboarding')}
            bikeType={bikeType}
            ridingStyle={ridingStyle}
            experience={experience}
            flexibility={flexibility}
            painPoints={painPoints}
          />
          {processingError && (
            <div style={{
              marginTop: '20px',
              padding: '15px',
              backgroundColor: '#fee',
              border: '1px solid #c00',
              borderRadius: '5px',
              color: '#c00',
            }}>
              <strong>Previous Error:</strong> {processingError}
            </div>
          )}
        </>
      )}

      {dashState === 'processing' && (
        <ProcessingStatus
          progress={processingProgress}
          stage={processingStage}
        />
      )}

      {dashState === 'results' && sessionId && (
        <FitResultsPage
          sessionId={sessionId}
          sideFrames={sideFrames}
          frontFrames={frontFrames}
          pedalAnalysis={pedalAnalysis}
          bikeType={bikeType}
          ridingStyle={ridingStyle}
          experience={experience}
          flexibility={flexibility}
          painPoints={painPoints}
          isSignedIn={isSignedIn === true}
          onStartOver={handleStartOver}
          onCreditConsumed={() => setHasBikeFit(false)}
        />
      )}
    </>
  );
};

export default DashboardPage;
